import { Chip } from "@dash/form";
import { Tile, TileStripe } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { MdPallet } from "react-icons/md";
import { Link } from "react-router-dom-v5-compat";

import { ItemDoc } from "@megaron/catalog-contracts";
import { Order } from "@megaron/fulfillment-contracts";

import { getOrderPackingStatus } from "./OrdersTable";
import { statusTitleMap } from "./packing-page/PackageList";

type Props = {
  order: Order;
  itemDocs: ItemDoc[];
};

type ChipProps = {
  color: string;
  variant: "primary" | "secondary" | "outline";
};

const getChipProps = (status: string, theme: ReturnType<typeof useTheme>): ChipProps => {
  if (status === "anulowane") return { color: theme.colors.danger, variant: "secondary" };
  if (status === "zrealizowane") return { color: theme.colors.success, variant: "primary" };
  if (status === "niezapakowane") return { color: theme.colors.default, variant: "outline" };
  if (status === "zapakowane") return { color: theme.colors.success, variant: "secondary" };
  if (status === "rozpoczęto pakowanie") return { color: theme.colors.warning, variant: "secondary" };
  return { color: theme.colors.default, variant: "outline" };
};

const normalizeGtin = (s?: string | null) => (s ?? "").replace(/\D/g, "").replace(/^0+/, "");

export const OrderTile: React.FC<Props> = ({ order, itemDocs }) => {
  const theme = useTheme();
  const statusBase = getOrderPackingStatus(order);
  const status = order.cancelledAt ? "anulowane" : statusBase;
  const chip = getChipProps(status, theme);
  const inpostStatuses = order.packages.map(
    (p) => statusTitleMap[p.trackingStatus as string] ?? p.trackingStatus ?? "brak",
  );
  const inpostStatusLine = inpostStatuses.length === 0 ? "brak" : inpostStatuses.join(" • ");

  const getDisplayNameFromDocs = (barcodeOrGtin: string | undefined | null, fallbackName: string) => {
    const code = normalizeGtin(barcodeOrGtin ?? null);
    if (!code) return fallbackName;
    const match = itemDocs.find((x: any) => normalizeGtin(x.gtin) === code);
    return match?.itemName ?? fallbackName;
  };

  const productChips =
    ((order as any).items ?? []).map((i: any) => {
      const qty = (i.quantityToPack ?? i.toPack ?? i.quantity ?? 0) as number;
      const fallback = (i.shortName ?? i.name ?? i.title ?? "") as string;
      const name = getDisplayNameFromDocs(i.barcode ?? (i as any)?.gtin ?? null, fallback);
      return `${name} (${qty})`;
    }) ?? [];

  return (
    <Link to={`/specjalista/order-fulfillment/id/${order.id}`}>
      <Tile stripe={<TileStripe color={theme.colors.primary} />}>
        <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", fontSize: "0.875rem" }}>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <h3 css={{ margin: 0, fontSize: "0.9375rem", minWidth: 0 }}>
                <div css={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                  <strong
                    css={{
                      display: "block",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`WEB-${order.orderNumber}`}
                  </strong>
                  {order.deliveryMethod == "palletDelivery" && <MdPallet />}
                </div>
              </h3>
              <span
                css={{ fontSize: "0.75rem", color: theme.colors.secondaryText, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {order.processedAt.toLocaleString()}
              </span>
            </div>

            <Chip color={chip.color} variant={chip.variant}>
              {status}
            </Chip>
          </div>

          <div css={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {productChips.length === 0 ? (
              <Chip color={theme.colors.primary} variant="primary">
                brak
              </Chip>
            ) : (
              productChips.map((label: string, idx: number) => (
                <Chip key={idx} color={theme.colors.primary} variant="primary">
                  {label}
                </Chip>
              ))
            )}
          </div>

          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <span>inpost status:</span>
            <span
              css={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                flex: 1,
              }}
            >
              {inpostStatusLine}
            </span>
          </div>
        </div>
      </Tile>
    </Link>
  );
};
